import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Meteor } from 'meteor/meteor';
import { useTracker } from 'meteor/react-meteor-data';
import { strings } from '/imports/strings';
import { Button } from '/imports/ui/Button';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { PageFooter } from '/imports/ui/PageFooter';
import { Form } from '/imports/ui/Form';
import { FormField } from '/imports/ui/FormField';
import { Input } from '/imports/ui/Input';
import { SmaAccessToken, getSmaAccessTokensForOrg, pullSmaPlantsForToken } from '/imports/api/smaMeters';
import { toastOnError } from '/imports/ui/utils';
import { Loading } from '/imports/ui/Loading';

const SmaMeterPageStyled = styled(Page)`
  .result {
    margin: 2rem 0;
    .buttons {
      display: flex;
      gap: 1rem;
      margin-top: 1rem;
    }
  }
  table {
    td {
      padding: 1rem;
      &:first-child {
        padding-left: 0;
      }
    }
  }
`;

/** Superuser page to build SMA OAuth URLs and pull plants for existing access tokens. */
export const SmaMeterPage = () => {
  const currentUser = useTracker(() => Meteor.user());
  if (!currentUser?.isSuperUser) return <NotPermittedPage />;
  const orgId = currentUser.orgId;
  const [oauthUrl, setOauthUrl] = useState<string>();
  const [username, setUsername] = useState<string>();
  const [accessTokens, setAccessTokens] = useState<SmaAccessToken[]>([]);
  const [pullingTokens, setPullingTokens] = useState<string[]>([]);
  const { authorizeUrl, redirectUrl, clientId } = Meteor.settings.public.sma;
  useEffect(() => {
    if (!orgId || !username) return;
    const params = new URLSearchParams();
    params.append('response_type', 'code');
    params.append('client_id', clientId);
    params.append('redirect_uri', redirectUrl);
    params.append('state', window.btoa(JSON.stringify({ username, orgId })));
    setOauthUrl(`${authorizeUrl}?${params.toString()}`);
  }, [orgId, username]);
  useEffect(() => {
    (async () => {
      if (!orgId) return;
      setAccessTokens(await getSmaAccessTokensForOrg.call(orgId));
    })();
  }, [orgId]);
  /**
   * Pulls SMA plants for the given access token and toasts the found/created counts.
   * @param tokenId - access token id to pull plants for
   */
  const pullPlants = toastOnError(async (tokenId: string) => {
    setPullingTokens([...pullingTokens, tokenId]);
    const { numPlants, numCreated } = await pullSmaPlantsForToken.call(tokenId);
    toast.success(`${numPlants} ${strings.SystemsFound()} | ${numCreated} ${strings.Created()}`);
    setPullingTokens(pullingTokens.filter((t) => t !== tokenId));
  });
  return (
    <SmaMeterPageStyled className="page">
      <PageHeading left={<h1>SMA</h1>} />
      <PageBody>
        <Form>
          <div className="field-group">
            <FormField field="username">
              <label>{strings.Username()}</label>
              <Input value={username} onChange={(username) => setUsername(username)} />
            </FormField>
          </div>
        </Form>
        {
          oauthUrl ?
            <div className="result">
              <h2>OAuth URL</h2>
              <div>{oauthUrl}</div>
              <div className="buttons">
                <Button onClick={() => navigator.clipboard.writeText(oauthUrl)}>{strings.Copy()}</Button>
                <a href={oauthUrl} target="_blank" rel="noreferrer"><Button>{strings.Visit()}</Button></a>
              </div>
            </div>
            :
            null
        }
        {
          accessTokens.length ?
            <div>
              <h2>{strings.Accounts()}</h2>
              <table>
                <tbody>
                  {
                    accessTokens.map((t) => (
                      <tr key={t._id}>
                        <td>{t.username}</td>
                        <td><Button onClick={() => pullPlants(t._id)}>{pullingTokens.includes(t._id) ? <Loading /> : strings.PullSystems()}</Button></td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
            :
            null
        }
      </PageBody>
      <PageFooter />
    </SmaMeterPageStyled>
  );
};
